import { useContext, useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, Drawer } from "rsuite";
import { PropertyStoreContext } from "../../contexts/PropertyStoreContext";
import PriceCheckForm from "../../components/priceCheck-form/PriceCheckForm";
import PriceCheckShowHideColumnModal from "../../components/priceCheck-form/PriceCheckShowHideColumnModal";
import PriceCheckMainContent from "../../components/priceCheck-form/PriceCheckMainContent";
import {
  DEFAULT_COLUMNS,
  getSelectedPropertyObjects,
  getSelectedPropertyExpediaHotelIds,
  mergePropertiesById,
} from "./utils";
import {
  persistSearchBasedPropertyNames,
  getInitialFormValues,
} from "../../components/priceCheck-form/PriceCheckFormUtils";
import { clearStoredFilterValues } from "../../components/priceCheck-form/PriceCheckFilterUtils";
import { usePriceCheckSearch } from "../../hooks/usePriceCheckSearch";
import { usePriceCheckAutoSearch } from "../../hooks/usePriceCheckAutoSearch";
import { usePriceCheckHandlers } from "../../hooks/usePriceCheckHandlers";
import { usePriceCheckTabChange } from "../../hooks/usePriceCheckTabChange";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import "./priceCheck.scss";

const PriceCheck = ({ initialEdit = true, initialPriceCheckData = [] }) => {
  const { properties, selectedProperty, isPilotHotel } =
    useContext(PropertyStoreContext);

  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  const [isShowHideColumn, setIsShowHideColumn] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  // Initialize searchBasedPropertyObjects from localStorage or empty array
  // Store property objects (with ID and name) instead of just names
  const [searchBasedPropertyObjects, setSearchBasedPropertyObjects] = useState(
    () => {
      try {
        const saved = localStorage.getItem(
          "priceCheck_searchBasedPropertyNames"
        );
        if (saved) {
          const parsed = JSON.parse(saved);
          // Handle backward compatibility: if it's an array of strings (old format),
          // convert to array of objects. Otherwise, assume it's already objects.
          if (Array.isArray(parsed) && parsed.length > 0) {
            if (typeof parsed[0] === "string") {
              // Old format: array of property names, convert to objects
              return parsed.map((name) => ({
                propertyName: name,
                propertyId: name,
              }));
            }
            return parsed;
          }
        }
        return [];
      } catch (error) {
        getLogger().warn("PriceCheck", {
          type: "Warn",
          page_url: window.location.href,
          error_desc: "Parse warning for saved properties",
          error_stack: error?.stack ?? null,
        });
        return [];
      }
    }
  );

  // Store form values that were used for the search (for daily breakdown)
  const [searchFormValues, setSearchFormValues] = useState(null);

  // Persist searchBasedPropertyObjects to localStorage whenever it changes
  useEffect(() => {
    persistSearchBasedPropertyNames(searchBasedPropertyObjects);
  }, [searchBasedPropertyObjects]);

  // The context `properties` only holds the first (server-side) page. Merge in
  // the objects captured for the current search and the header selection so
  // property-name and hotel-id resolution work for any selected property, not
  // just those on the first page.
  const augmentedProperties = useMemo(
    () =>
      mergePropertiesById(
        properties,
        searchBasedPropertyObjects,
        selectedProperty ? [selectedProperty] : []
      ),
    [properties, searchBasedPropertyObjects, selectedProperty]
  );

  // Search orchestration hook
  const searchState = usePriceCheckSearch({
    initialEdit,
    initialPriceCheckData,
    properties: augmentedProperties,
  });
  useEffect(() => {
    if (
      !augmentedProperties ||
      augmentedProperties.length === 0 ||
      !searchState.formValues ||
      !searchState.hasSearchResults ||
      searchState.isEdit ||
      searchState.isLoading
    ) {
      return;
    }
    const updatedPropertyObjects = getSelectedPropertyObjects(
      augmentedProperties,
      searchState.formValues.selectedCity,
      searchState.formValues.selectedProperties
    );
    if (
      updatedPropertyObjects.length > 0 &&
      JSON.stringify(updatedPropertyObjects) !==
        JSON.stringify(searchBasedPropertyObjects)
    ) {
      setSearchBasedPropertyObjects(updatedPropertyObjects);
    }
  }, [
    augmentedProperties,
    searchState.formValues,
    searchState.hasSearchResults,
    searchState.isEdit,
    searchState.isLoading,
    searchBasedPropertyObjects,
  ]);

  // Auto-search logic hook.
  // Use the stable context `properties` (not augmentedProperties): this hook's
  // effect restores saved form values whenever `properties` changes, so feeding
  // it the churning augmented list would reset the user's in-progress edits
  // (e.g. wiping a property they just ticked in Edit Search).
  usePriceCheckAutoSearch({
    properties,
    initialEdit,
    hasAutoSearched: searchState.hasAutoSearched,
    setHasAutoSearched: searchState.setHasAutoSearched,
    autoSearchInitiatedRef: searchState.autoSearchInitiatedRef,
    handleSearchRef: searchState.handleSearchRef,
    setFormValues: searchState.setFormValues,
  });

  // Event handlers hook
  const handlers = usePriceCheckHandlers({
    formValues: searchState.formValues,
    handleSearch: searchState.handleSearch,
    priceCheckPage: searchState.priceCheckPage,
    setPriceCheckPage: searchState.setPriceCheckPage,
    sortState: searchState.sortState,
    setSortState: searchState.setSortState,
    selectedFilters: searchState.selectedFilters,
    setSelectedFilters: searchState.setSelectedFilters,
    filterSelectionOrder: searchState.filterSelectionOrder,
    setFilterSelectionOrder: searchState.setFilterSelectionOrder,
    activeTab,
    properties: augmentedProperties,
    setExpandedRowKeys,
  });

  // Tab change handler hook
  const tabHandlers = usePriceCheckTabChange({
    formValues: searchState.formValues,
    handleSearch: searchState.handleSearch,
    priceCheckPage: searchState.priceCheckPage,
    properties: augmentedProperties,
    setSelectedFilters: searchState.setSelectedFilters,
    setFilterSelectionOrder: searchState.setFilterSelectionOrder,
    setPriceCheckPage: searchState.setPriceCheckPage,
    selectedFilters: searchState.selectedFilters,
    filterSelectionOrder: searchState.filterSelectionOrder,
    activeTab,
    setExpandedRowKeys,
  });

  const handleToggleExpand = (rowKey) => {
    setExpandedRowKeys((prev) =>
      prev.includes(rowKey)
        ? prev.filter((key) => key !== rowKey)
        : [...prev, rowKey]
    );
  };

  // Use a ref to track if we should skip form restoration on next close
  const skipFormRestorationRef = useRef(false);

  // Handler for when drawer closes (either cancel or after search)
  const handleCloseDrawer = () => {
    // Only restore form state if we haven't just completed a search
    if (!skipFormRestorationRef.current) {
      // Restore form to the previous searched state when closing drawer without searching
      if (searchFormValues) {
        // Use the actual form values that were used for the current search results
        searchState.setFormValues(searchFormValues);
      } else {
        // Fallback: get the last saved search state from localStorage using existing utility
        const savedFormValues = getInitialFormValues(augmentedProperties);
        if (savedFormValues) {
          searchState.setFormValues(savedFormValues);
        }
        // If no saved values, keep current form values
      }
    }

    // Reset the flag and close drawer
    skipFormRestorationRef.current = false;
    setIsDrawerOpen(false);
  };

  // Wrapper function to reset active tab and filters when search is performed
  const handleSearchWithTabReset = (values) => {
    // Reset active tab to "all properties"
    setActiveTab("all");

    // Close all expanded rows when performing a new search
    setExpandedRowKeys([]);

    // Clear stored filter values and skip persistence during reset
    clearStoredFilterValues();
    searchState.setSkipFilterPersistence(true);

    // Clear all tab-specific filter states
    tabHandlers.clearAllTabFilterStates();

    // Reset filters to default values
    const defaultFilters = {
      ratePlans: ["all"],
      roomTypes: ["all"],
      mealPlans: ["all"],
      promotions: ["all"],
      refundableOptions: ["all"],
    };

    searchState.setSelectedFilters(defaultFilters);
    searchState.setFilterSelectionOrder([]);
    searchState.setSearchCounter((prev) => prev + 1);

    // Reset PriceCheckPage to 1 (0-based internally)
    searchState.setPriceCheckPage((prevState) => ({
      ...prevState,
      number: 0,
    }));

    // Re-enable filter persistence immediately after state updates
    searchState.setSkipFilterPersistence(false);

    // Store the form values used for this search
    setSearchFormValues(values);

    // Resolve using the full objects captured from the picker first (they carry
    // expediaHotelId for properties beyond the first page), then the augmented
    // list. Prefer the picker objects on conflict.
    const resolutionProperties = mergePropertiesById(
      values.selectedPropertyObjects,
      augmentedProperties
    );

    // Update tabs and perform search
    const propertyObjects = getSelectedPropertyObjects(
      resolutionProperties,
      values.selectedCity,
      values.selectedProperties
    );
    setSearchBasedPropertyObjects(propertyObjects);

    // Pass hotel ids explicitly so the immediate search doesn't rely on the
    // not-yet-committed searchBasedPropertyObjects state update above.
    const hotelIds = getSelectedPropertyExpediaHotelIds(
      resolutionProperties,
      values.selectedCity || [],
      values.selectedProperties || []
    );
    searchState.handleSearch(
      values,
      null,
      defaultFilters,
      [],
      false,
      hotelIds.length ? hotelIds : null
    );
  };

  return (
    <Grid fluid className="price-check-form-container">
      {searchState.isEdit ? (
        <PriceCheckForm
          onSubmit={handleSearchWithTabReset}
          onClose={() => searchState.setIsEdit(false)}
        />
      ) : (
        <PriceCheckMainContent
          formValues={searchState.formValues}
          searchFormValues={searchFormValues}
          properties={augmentedProperties}
          isPilotHotel={isPilotHotel}
          setIsDrawerOpen={setIsDrawerOpen}
          setIsShowHideColumn={setIsShowHideColumn}
          filtersData={searchState.filtersData}
          onFiltersChange={handlers.handleFiltersChange}
          sortState={searchState.sortState}
          currentFilters={searchState.selectedFilters}
          currentFilterSelectionOrder={searchState.filterSelectionOrder}
          searchCounter={searchState.searchCounter}
          isLoading={searchState.isLoading}
          priceCheckData={searchState.priceCheckData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          columns={columns}
          priceCheckPage={searchState.priceCheckPage}
          onPageChange={handlers.onPageChange}
          expandedRowKeys={expandedRowKeys}
          handleToggleExpand={handleToggleExpand}
          handleSort={handlers.handleSort}
          setHasSearchResults={searchState.setHasSearchResults}
          onTabChange={tabHandlers.handleTabChange}
          setPriceCheckPage={searchState.setPriceCheckPage}
          searchBasedPropertyObjects={searchBasedPropertyObjects}
          tabHandlers={tabHandlers}
        />
      )}
      <Drawer
        placement="top"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        closeButton={false}
        className="price-check-drawer"
      >
        <Drawer.Body>
          <div className="price-check-form-container">
            <PriceCheckForm
              onSubmit={(values) => {
                handleSearchWithTabReset(values);
                // Set flag to skip form restoration on the next drawer close
                skipFormRestorationRef.current = true;
                setIsDrawerOpen(false);
              }}
              onClose={handleCloseDrawer}
              source="drawer"
              formValues={searchState.formValues}
              setFormValues={searchState.setFormValues}
              properties={augmentedProperties}
            />
          </div>
        </Drawer.Body>
      </Drawer>
      <PriceCheckShowHideColumnModal
        isShowHideColumn={isShowHideColumn}
        setIsShowHideColumn={setIsShowHideColumn}
        settingColumn={setColumns}
        columns={columns}
      />
    </Grid>
  );
};

PriceCheck.propTypes = {
  initialEdit: PropTypes.bool,
  initialPriceCheckData: PropTypes.array,
};

export default PriceCheck;
